import React, { useState, useEffect, useCallback } from 'react';
import { Users, RefreshCw, Search, Shield, ShieldCheck, ShieldOff, UserCheck, UserX, Key, UserPlus, Send, Ban, KeyRound } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { OPEN_INVITE_STATES, INVITE_STATE_LABEL, deliveryOutcome, deliveryToast } from '../../lib/invitationDelivery';
import {
  fetchRootStatus, NO_ROOT_STATUS, roleLabel, isRootRole,
  ROLE_ROOT, ROLE_SUPERADMIN, rootFetch, stepUpChallenge,
} from '../../lib/rootSecurity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function safeFetchJson(path, options = {}) {
  const url = `${BACKEND_URL}${path}`;
  const token =
    localStorage.getItem('superadmin_session') ||
    localStorage.getItem('superadmin_session_token') ||
    localStorage.getItem('superAdminToken') ||
    localStorage.getItem('superadminToken');

  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(url, { credentials: 'include', ...options, headers }).then((res) => {
    if (!res.ok) return res.text().then((t) => { throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`); });
    return res.json();
  });
}

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-800',
  operator: 'bg-blue-100 text-blue-800',
  manager: 'bg-green-100 text-green-800',
  super_admin: 'bg-red-100 text-red-800',
  root_admin: 'bg-slate-900 text-white',
};

const STATUS_META = {
  active: { label: 'Active', cls: 'bg-green-100 text-green-800' },
  pending_invitation: { label: 'Pending activation', cls: 'bg-amber-100 text-amber-800' },
  mfa_pending: { label: 'MFA pending', cls: 'bg-blue-100 text-blue-800' },
  deactivated: { label: 'Deactivated', cls: 'bg-red-100 text-red-800' },
};

const INVITE_STATE_META = {
  pending_delivery: { label: 'Sending invite…', cls: 'bg-slate-100 text-slate-700' },
  delivery_failed: { label: 'Delivery failed', cls: 'bg-red-100 text-red-800' },
  pending_activation: { label: 'Pending activation', cls: 'bg-amber-100 text-amber-800' },
  // Phase 2D — governed staging non-delivery: the invitee did NOT receive a link. Distinct from
  // "Pending activation" (reserved for a real send) and from a hard "Delivery failed".
  delivery_redirected: { label: INVITE_STATE_LABEL.delivery_redirected, cls: 'bg-yellow-100 text-yellow-800' },
  delivery_suppressed: { label: INVITE_STATE_LABEL.delivery_suppressed, cls: 'bg-yellow-100 text-yellow-800' },
  mfa_pending: { label: 'MFA pending', cls: 'bg-blue-100 text-blue-800' },
  expired: { label: 'Invite expired', cls: 'bg-orange-100 text-orange-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-800' },
};

const NEEDS_SETUP_CLASSES = ['password_required', 'password_and_totp_required'];

// Truthful status pill: live invitation state → incomplete-onboarding (no password) → account status.
function statusDisplay(user) {
  const inv = user.invitation;
  if (inv && INVITE_STATE_META[inv.state]) return INVITE_STATE_META[inv.state];
  const p = user.provisioning;
  if (p && NEEDS_SETUP_CLASSES.includes(p.classification)) {
    return { label: 'Incomplete setup', cls: 'bg-orange-100 text-orange-800' };
  }
  const st = user.account_status || (user.is_active !== false ? 'active' : 'deactivated');
  return STATUS_META[st] || { label: st, cls: 'bg-gray-100 text-gray-700' };
}

const UserManagement = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: ROLE_SUPERADMIN, mfa_enabled: false });
  // Server-resolved tier. NO_ROOT_STATUS until proven otherwise, and fetchRootStatus fails
  // closed, so a failed lookup never draws a Root-only control.
  const [rootStatus, setRootStatus] = useState(NO_ROOT_STATUS);
  const [pendingStepUp, setPendingStepUp] = useState(null);   // { label, retry }
  const [stepUpCode, setStepUpCode] = useState('');
  const [stepUpBusy, setStepUpBusy] = useState(false);

  const createUser = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast({ title: 'Missing fields', description: 'Name, email and phone are required.', variant: 'destructive' });
      return;
    }
    // Snapshot the entered values so a parked retry replays exactly this invitation.
    const payload = { ...form };
    // Creating a platform account requires a fresh Root TOTP step-up (backend require_step_up, unchanged).
    // Reuse the EXISTING Make Root mechanism verbatim: rootFetch surfaces the structured
    // MFA_STEP_UP_REQUIRED detail, the shared page-level prompt collects the code, and submitStepUp verifies
    // then replays `run` exactly once. No new MFA component; the backend guard is honoured, never bypassed.
    const run = async () => {
      const res = await rootFetch('/api/v1/super-admin/users', {
        method: 'POST', body: JSON.stringify(payload),
      });
      const dstat = res && res.invitation && res.invitation.delivery_status;
      toast(deliveryToast(dstat, 'sent'));
      setForm({ name: '', email: '', phone: '', role: ROLE_SUPERADMIN, mfa_enabled: false });
      await fetchUsers();
    };
    setCreating(true);
    try {
      await run();
      setAddOpen(false);
    } catch (err) {
      if (stepUpChallenge(err)) {
        // Close the invite modal so the EXISTING page-level step-up prompt (the same one Make Root uses) is
        // visible, then park the invitation for submitStepUp to replay once after the code verifies.
        setAddOpen(false);
        setPendingStepUp({ label: `Invite ${payload.email || 'SuperAdmin'}`, retry: run });
      } else {
        toast({ title: 'Invite failed', description: typeof err.detail === 'string' ? err.detail : err.message, variant: 'destructive' });
      }
    } finally {
      setCreating(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await safeFetchJson('/api/v1/super-admin/users?limit=200');
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { let live = true;
    fetchRootStatus().then((st) => { if (live) setRootStatus(st); });
    return () => { live = false; }; }, []);

  const manageUser = async (userId, action) => {
    setActionLoading(`${userId}-${action}`);
    try {
      await safeFetchJson(`/api/v1/super-admin/users/${userId}/manage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const label = action === 'unlock_account' ? 'Account unlocked'
        : action === 'reset_mfa' ? 'MFA reset — the user must enrol an authenticator again'
          : action === 'deactivate' ? 'User deactivated' : action === 'activate' ? 'User activated' : `${action} completed`;
      toast({ title: 'Success', description: label });
      await fetchUsers();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Governed CASE-B restart: revoke unproven MFA + supersede stale invitation + re-send ONE fresh setup
  // link; the account returns to Pending until the user accepts and enrols. Requires reason + confirm.
  const restartOnboarding = async (userId) => {
    const reason = window.prompt('Restart onboarding — reason (recorded in the audit log). This revokes the account’s MFA + any active invitation and re-sends a fresh setup link; the account returns to Pending until the user completes setup:');
    if (reason === null) return;
    if (!reason.trim()) { toast({ title: 'Reason required', description: 'Please provide a reason.', variant: 'destructive' }); return; }
    if (!window.confirm('Restart onboarding for this SuperAdmin? Their access is revoked until they accept a fresh invitation and enrol an authenticator.')) return;
    setActionLoading(`${userId}-restart`);
    try {
      const res = await safeFetchJson(`/api/v1/super-admin/users/${userId}/restart-onboarding`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim(), confirm: true }),
      });
      const dstat = res && res.invitation && res.invitation.delivery_status;
      toast(deliveryToast(dstat, 'setup'));
      await fetchUsers();
    } catch (err) {
      toast({ title: 'Restart failed', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const resendInvite = async (invitationId) => {
    setActionLoading(`${invitationId}-resend`);
    try {
      const res = await safeFetchJson(`/api/v1/super-admin/invitations/${invitationId}/resend`, { method: 'POST' });
      const dstat = res && res.invitation && res.invitation.delivery_status;
      toast(deliveryToast(dstat, 'resent'));
      await fetchUsers();
    } catch (err) {
      toast({ title: 'Resend failed', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const cancelInvite = async (invitationId) => {
    if (!window.confirm('Cancel this invitation? The activation link will stop working and the pending account will be deactivated.')) return;
    setActionLoading(`${invitationId}-cancel`);
    try {
      await safeFetchJson(`/api/v1/super-admin/invitations/${invitationId}/cancel`, { method: 'POST' });
      toast({ title: 'Invitation cancelled', description: 'The activation link has been invalidated.' });
      await fetchUsers();
    } catch (err) {
      toast({ title: 'Cancel failed', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Re-send a fresh setup link to an EXISTING incomplete-onboarding admin (no password / never activated).
  const reinviteUser = async (userId) => {
    const reason = window.prompt('Reason for re-sending the setup link (recorded in the audit log):');
    if (reason === null) return; // cancelled
    if (!reason.trim()) { toast({ title: 'Reason required', description: 'Please provide a reason.', variant: 'destructive' }); return; }
    if (!window.confirm('Send this administrator a fresh setup link? Their account stays pending until they create a password and enrol an authenticator.')) return;
    setActionLoading(`${userId}-reinvite`);
    try {
      const res = await safeFetchJson(`/api/v1/super-admin/users/${userId}/reinvite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim(), confirm: true }),
      });
      const dstat = res && res.invitation && res.invitation.delivery_status;
      toast(deliveryToast(dstat, 'setup'));
      await fetchUsers();
    } catch (err) {
      toast({ title: 'Re-send failed', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Root assignment / demotion. Rendered only for a caller the SERVER says may assign Root; the
  // route re-checks, so this is presentation, never the boundary. Promotion is declared high-risk,
  // so a fresh authenticator code is demanded and the action replayed exactly once after it.
  const changeRole = async (userId, newRole, userLabel) => {
    const promoting = isRootRole(newRole);
    const verb = promoting ? `Promote ${userLabel} to Root Administrator` : `Change ${userLabel} to Super Administrator`;
    if (!window.confirm(`${verb}? ${promoting
      ? 'Root can approve external staging e-mail recipients and manage other Root accounts.'
      : 'They keep every Super Administrator capability.'}`)) return;

    const run = () => rootFetch(`/api/v1/super-admin/users/${userId}/manage`, {
      method: 'POST', body: JSON.stringify({ action: 'change_role', new_role: newRole }),
    });
    setActionLoading(`${userId}-role`);
    try {
      await run();
      toast({ title: 'Role updated', description: `${userLabel} is now ${roleLabel(newRole)}.` });
      await fetchUsers();
    } catch (err) {
      if (stepUpChallenge(err)) {
        setPendingStepUp({ label: verb, retry: run });
      } else {
        toast({ title: 'Role change failed', description: typeof err.detail === 'string' ? err.detail : err.message, variant: 'destructive' });
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Existing platform step-up authority: verify, then replay the parked action once.
  const submitStepUp = async () => {
    if (!/^\d{6}$/.test(stepUpCode.trim())) {
      toast({ title: 'Enter a code', description: 'Six digits from your authenticator app.', variant: 'destructive' });
      return;
    }
    setStepUpBusy(true);
    try {
      await rootFetch('/api/v1/super-admin/security/step-up', {
        method: 'POST', body: JSON.stringify({ code: stepUpCode.trim() }),
      });
    } catch (err) {
      toast({ title: 'Code not accepted', description: typeof err.detail === 'string' ? err.detail : err.message, variant: 'destructive' });
      setStepUpBusy(false);
      return;
    }
    const parked = pendingStepUp;
    setPendingStepUp(null); setStepUpCode(''); setStepUpBusy(false);
    if (!parked) return;
    try {
      await parked.retry();
      toast({ title: 'Done', description: `${parked.label} — completed.` });
      await fetchUsers();
    } catch (err) {
      toast({ title: 'Action failed', description: typeof err.detail === 'string' ? err.detail : err.message, variant: 'destructive' });
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return !q || (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage system users and roles</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAddOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
            <UserPlus className="h-4 w-4 mr-2" />
            Add SuperAdmin User
          </Button>
          <Button onClick={fetchUsers} variant="outline" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <div className="text-2xl font-bold text-gray-900">{users.length}</div>
          <div className="text-sm text-gray-500">Total Users</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <div className="text-2xl font-bold text-green-600">{users.filter((u) => (u.account_status || (u.is_active !== false ? 'active' : 'x')) === 'active').length}</div>
          <div className="text-sm text-gray-500">Active</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 border">
          <div className="text-2xl font-bold text-amber-600">{users.filter((u) => u.invitation && OPEN_INVITE_STATES.includes(u.invitation.state)).length}</div>
          <div className="text-sm text-gray-500">Pending invitation</div>
        </div>
      </div>

      {pendingStepUp && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="root-step-up">
          <p className="text-sm text-blue-900 flex items-center gap-2 mb-2">
            <KeyRound className="h-4 w-4" />
            {pendingStepUp.label} needs a fresh authenticator code before it can proceed.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              className="border rounded-md px-3 py-2 text-sm w-40"
              inputMode="numeric" maxLength={6} placeholder="000000"
              aria-label="Authenticator code"
              value={stepUpCode} onChange={(e) => setStepUpCode(e.target.value.replace(/\D/g, ''))}
            />
            <Button size="sm" onClick={submitStepUp} disabled={stepUpBusy}>
              {stepUpBusy ? 'Verifying…' : 'Verify and continue'}
            </Button>
            <Button size="sm" variant="outline" disabled={stepUpBusy}
              onClick={() => { setPendingStepUp(null); setStepUpCode(''); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          className="pl-9"
          placeholder="Search by name, email or role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading && (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-purple-400" />
            Loading users...
          </div>
        )}
        {error && !loading && (
          <div className="p-8 text-center">
            <p className="text-red-600 font-medium">{error}</p>
            <Button onClick={fetchUsers} className="mt-4" variant="outline">Retry</Button>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>{search ? 'No users match your search.' : 'No users found.'}</p>
          </div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">MFA</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((user) => {
                const userId = user.id || user._id;
                const inv = user.invitation;
                const isPending = inv && OPEN_INVITE_STATES.includes(inv.state);
                const effStatus = user.account_status || (user.is_active !== false ? 'active' : 'deactivated');
                const isActive = effStatus === 'active';
                const needsSetup = !isPending && user.provisioning && NEEDS_SETUP_CLASSES.includes(user.provisioning.classification);
                const sd = statusDisplay(user);
                // A Root target may only be mutated by another Root. This hides lifecycle controls
                // ONLY for a Root row seen by a non-Root caller — an ordinary SuperAdmin keeps every
                // action they had against ordinary SuperAdmins.
                const targetIsRoot = isRootRole(user.role);
                const protectedFromMe = targetIsRoot && !rootStatus.can_manage_protected_accounts;
                // GOV13-G — authoritative per-action eligibility comes from the backend (`allowed_actions`).
                // The UI only renders what the server allows; the mutation routes re-check. Fallback keeps a
                // sensible render during a mixed-revision canary; the backend remains the boundary.
                const aa = user.allowed_actions || {
                  deactivate: { allowed: isActive },
                  unlock_account: { allowed: !!user.account_locked },
                  reset_mfa: { allowed: !!(user.mfa_enabled || (user.provisioning && user.provisioning.authenticator_enrolled)) },
                  restart_onboarding: { allowed: isActive && !(user.last_login || user.onboarding_completed || user.invitation_accepted) },
                };
                return (
                  <tr key={userId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-semibold text-xs">
                          {(user.name || user.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.name || '—'}</div>
                          <div className="text-gray-500 text-xs">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={ROLE_COLORS[user.role] || 'bg-gray-100 text-gray-700'}>
                        {roleLabel(user.role)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={sd.cls}>{sd.label}</Badge>
                      {isPending && inv.delivery_status && !deliveryOutcome(inv.delivery_status).reached && inv.delivery_status !== 'pending' && (
                        <div className={`text-[10px] mt-1 ${deliveryOutcome(inv.delivery_status).tone === 'warning' ? 'text-yellow-700' : 'text-red-500'}`}>
                          {deliveryOutcome(inv.delivery_status).detail || 'Invitee did not receive the link'}
                        </div>
                      )}
                      {needsSetup && (
                        <div className="text-[10px] text-orange-600 mt-1">
                          {user.provisioning.password_configured ? null : 'No password configured'}
                          {!user.provisioning.password_configured && !user.provisioning.authenticator_enrolled ? ' · ' : ''}
                          {user.provisioning.authenticator_enrolled ? null : 'Authenticator not enrolled'}
                        </div>
                      )}
                      {!needsSetup && user.provisioning && user.provisioning.classification === 'totp_enrolment_required' && (
                        <div className="text-[10px] text-blue-600 mt-1">Authenticator enrolled at next sign-in</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.mfa_enabled ? (
                        <Shield className="h-4 w-4 text-green-600" />
                      ) : (
                        <span className="text-gray-400 text-xs">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {protectedFromMe ? (
                          <span className="text-[11px] text-gray-500 self-center" title="Root Administrator accounts can only be managed by another Root Administrator.">
                            Root Administrator — protected
                          </span>
                        ) : isPending ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                              disabled={!!actionLoading}
                              onClick={() => resendInvite(inv.invitation_id)}
                            >
                              <Send className="h-3 w-3 mr-1" />
                              {actionLoading === `${inv.invitation_id}-resend` ? '...' : 'Resend'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                              disabled={!!actionLoading}
                              onClick={() => cancelInvite(inv.invitation_id)}
                            >
                              <Ban className="h-3 w-3 mr-1" />
                              {actionLoading === `${inv.invitation_id}-cancel` ? '...' : 'Cancel'}
                            </Button>
                          </>
                        ) : needsSetup ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-orange-700 border-orange-200 hover:bg-orange-50"
                              disabled={!!actionLoading}
                              onClick={() => reinviteUser(userId)}
                            >
                              <Send className="h-3 w-3 mr-1" />
                              {actionLoading === `${userId}-reinvite` ? '...' : 'Re-send setup link'}
                            </Button>
                          </>
                        ) : (
                          <>
                            {isActive ? (
                              aa.deactivate && aa.deactivate.allowed ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                  disabled={!!actionLoading}
                                  onClick={() => manageUser(userId, 'deactivate')}
                                >
                                  <UserX className="h-3 w-3 mr-1" />
                                  {actionLoading === `${userId}-deactivate` ? '...' : 'Deactivate'}
                                </Button>
                              ) : (
                                <span className="text-[10px] text-gray-400 self-center" title={(aa.deactivate && aa.deactivate.reason) || 'Not available'}>
                                  {aa.deactivate && aa.deactivate.reason_code === 'last_recoverable_owner' ? 'Last owner — protected' : 'Protected'}
                                </span>
                              )
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-green-600 border-green-200 hover:bg-green-50"
                                disabled={!!actionLoading}
                                onClick={() => manageUser(userId, 'activate')}
                              >
                                <UserCheck className="h-3 w-3 mr-1" />
                                {actionLoading === `${userId}-activate` ? '...' : 'Activate'}
                              </Button>
                            )}
                            {aa.unlock_account && aa.unlock_account.allowed && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                disabled={!!actionLoading}
                                title="Clear the account lock and failed-login counter. Does not change credentials, MFA, or send email."
                                onClick={() => manageUser(userId, 'unlock_account')}
                              >
                                <Key className="h-3 w-3 mr-1" />
                                {actionLoading === `${userId}-unlock_account` ? '...' : 'Unlock account'}
                              </Button>
                            )}
                            {aa.reset_mfa && aa.reset_mfa.allowed && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                disabled={!!actionLoading}
                                title="Revoke the enrolled authenticator; the user must enrol MFA again at next sign-in."
                                onClick={() => { if (window.confirm('Reset MFA for this SuperAdmin? Their current authenticator is revoked and they must enrol a new one.')) manageUser(userId, 'reset_mfa'); }}
                              >
                                <Shield className="h-3 w-3 mr-1" />
                                {actionLoading === `${userId}-reset_mfa` ? '...' : 'Reset MFA'}
                              </Button>
                            )}
                            {aa.restart_onboarding && aa.restart_onboarding.allowed && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs text-orange-700 border-orange-200 hover:bg-orange-50"
                                disabled={!!actionLoading}
                                title="Governed restart: revoke MFA + any active invitation and re-send a fresh setup link. Account returns to Pending until re-onboarded."
                                onClick={() => restartOnboarding(userId)}
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                {actionLoading === `${userId}-restart` ? '...' : 'Restart onboarding'}
                              </Button>
                            )}
                          </>
                        )}
                        {rootStatus.can_assign_root && !isPending && (
                          targetIsRoot ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              disabled={!!actionLoading}
                              title="Return this account to Super Administrator. Refused if it is the last Root."
                              onClick={() => changeRole(userId, ROLE_SUPERADMIN, user.name || user.email)}
                            >
                              <ShieldOff className="h-3 w-3 mr-1" />
                              {actionLoading === `${userId}-role` ? '...' : 'Remove Root'}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-slate-900 border-slate-300 hover:bg-slate-100"
                              disabled={!!actionLoading}
                              title="Grant the platform owner tier. Requires a fresh authenticator code."
                              onClick={() => changeRole(userId, ROLE_ROOT, user.name || user.email)}
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              {actionLoading === `${userId}-role` ? '...' : 'Make Root'}
                            </Button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Add SuperAdmin User modal — real POST /super-admin/users (platform roles only) */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !creating && setAddOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Invite SuperAdmin User</h2>
            <p className="text-xs text-gray-500 mb-4">Servisfy platform owner/admin only. We email an activation link; the invitee sets a password and enrols multi-factor authentication before they can sign in. Not for tenant staff or customers.</p>
            <div className="space-y-3">
              <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Phone (+1...)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <select className="w-full border rounded-lg px-3 py-2 text-sm" aria-label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value={ROLE_SUPERADMIN}>{roleLabel(ROLE_SUPERADMIN)}</option>
                {/* Root was previously offered to every SuperAdmin — a one-click self-escalation
                    path straight out of this dropdown. Offered now only when the server says the
                    caller may assign it; the invite route refuses regardless of what is drawn. */}
                {rootStatus.can_assign_root && (
                  <option value={ROLE_ROOT}>{roleLabel(ROLE_ROOT)}</option>
                )}
              </select>
              <p className="flex items-center gap-2 text-xs text-gray-500">
                <Shield className="h-3.5 w-3.5 text-gray-400" />
                Multi-factor authentication is required for every platform administrator.
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setAddOpen(false)} disabled={creating}>Cancel</Button>
              <Button onClick={createUser} disabled={creating} className="bg-purple-600 hover:bg-purple-700 text-white">
                {creating ? 'Sending…' : 'Send invitation'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
